import React, { useMemo } from "react";

import { filter, getMetricValue, GraphProps } from "./base";
import Chart from "./Chart";

const MAX_GROUPS = 8;

interface TooltipParams {
    name: string;
    percent: number;
    value: number
}
export default function PieChart(props: GraphProps) {
    const options = useMemo(() => {
        const indexes = props.graph.dimensions?.map(row => props.columnIndexMap[row.fieldId]) ?? [];

        const dataRows = filter(props.data.rows, props.graph.filters, props.columnIndexMap);

        const rows = dataRows.groupBy(row => JSON.stringify(indexes.map(index => row.values[index])));

        const metric = props.graph.metrics?.firstElement;
        if (!metric) {
            return {
                title: "Invalid Metric",
            };
        }
        const metricIndex = props.columnIndexMap[metric.fieldId];

        const allGroups = Object.values(rows).mapFilter(group => {
            const first = group.firstElement;

            const dimensions: (string | number | undefined)[] = indexes.map(columnIndex => {
                const val = first.values[columnIndex];
                return props.labelMap[val ?? ""] ?? val;
            });

            const data = group.map(row => row.values[metricIndex]).filter(row => row != null && row !== "") as string[];

            return {
                value: getMetricValue(data, metric.aggregation),
                dataRow: data,
                name: dimensions.join(" - ") || "None",
            };
        }).sortByDescending("value");

        const rowData = allGroups.limit(MAX_GROUPS);

        const rest = allGroups.skip(MAX_GROUPS);
        if (rest.isNotEmpty) {
            const data = rest.flatMap(row => row.dataRow);
            rowData.push({
                value: getMetricValue(data, metric.aggregation),
                dataRow: data,
                name: "Others",
            });
        }

        return {
            tooltip: {
                trigger: "item",
                formatter: (params: TooltipParams) => `${params.name}: ${params.value} (${params.percent}%)`,
            },
            itemStyle: {
                borderRadius: 4,
            },
            series: [
                {
                    type: "pie",
                    radius: ["30%", "60%"],
                    data: rowData,
                    emphasis: {
                        itemStyle: {
                            shadowBlur: 10,
                            shadowOffsetX: 0,
                            shadowColor: "rgba(0, 0, 0, 0.5)",
                        },
                    },
                },
            ],
        };
    }, [props.data]);

    return (
        <div>
            {props.graph.title && (<strong className="d-block ms-1 text-light">{props.graph.title}</strong>)}

            <Chart options={options} />
        </div>
    );
}
